/* Harrod Outdoors: independently measured runs and optional, separately added trees. */
(function (root, factory) {
  'use strict';
  const calculator = factory();
  if (typeof module === 'object' && module.exports) module.exports = calculator;
  if (!root || !root.customElements || root.customElements.get('ho-ready-made-border')) return;

  class ReadyMadeBorder extends root.HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._controller = new AbortController();
      const start = () => this.initialise();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true, signal: this._controller.signal });
      } else start();
    }

    initialise() {
      if (!this.isConnected || this._initialised) return;
      this._initialised = true;
      const query = (selector) => this.querySelector(selector);
      this.form = query('[data-border-form]');
      this.button = query('[data-border-order]');
      try {
        this.config = JSON.parse(query('[data-border-config]').textContent);
        this.lengthInput = query('[data-border-length]');
        this.depthInput = query('[data-border-depth]');
        this.options = Array.from(this.querySelectorAll('[data-border-option]'));
        this.quantityInput = query('[data-border-quantity]');
        this.variantInput = query('[data-border-variant]');
        this.allocationInput = query('[data-border-allocation]');
        this.feedback = query('[data-border-feedback]');
        this.unitPrice = query('[data-border-unit-price]');
        this.runTotal = query('[data-border-run-total]');
        this.runs = query('[data-border-runs]');
        this.runList = query('[data-border-run-list]');
        this.basketLink = query('[data-border-basket-link]');
        this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: this.config.currency });
        this.session = this.session || new calculator.RunSession({ makeId: () => calculator.uniqueAllocation(root.crypto) });
        this.client = this.client || new calculator.CartAddClient({
          fetch: root.fetch.bind(root),
          endpoint: calculator.cartAddEndpoint(this.config.cartAddUrl, root.Shopify && root.Shopify.routes && root.Shopify.routes.root)
        });
        this._message = this._message || '';
        const listener = { signal: this._controller.signal };
        [this.lengthInput, this.depthInput].forEach((input) => {
          input.addEventListener('input', () => { this._message = ''; this.update(); }, listener);
          input.addEventListener('blur', () => { input.dataset.touched = 'true'; this.update(); }, listener);
        });
        this.options.forEach((select) => select.addEventListener('change', () => { this._message = ''; this.update(true); }, listener));
        // Stop the theme's generic quick-add handler from submitting the same form.
        this.form.addEventListener('submit', (event) => this.submit(event), { ...listener, capture: true });
        this.querySelectorAll('[data-border-thumbnail]').forEach((link) => link.addEventListener('click', (event) => {
          event.preventDefault(); this.selectImage(link);
        }, listener));
        this.initialiseGallery(listener);
        this.initialiseTrees(listener);
        this.update();
        this.renderRuns();
        const loading = query('[data-border-loading]');
        if (loading) loading.hidden = true;
      } catch (error) {
        if (this.button) { this.button.disabled = true; this.button.hidden = false; }
        const message = query('[data-border-loading]');
        if (message) { message.hidden = false; message.textContent = 'The calculator could not load. Please reload this page.'; }
        if (this.variantInput) this.variantInput.disabled = true;
        if (this.quantityInput) this.quantityInput.disabled = true;
      }
    }

    selectedVariant() {
      if (!this.options.length) return this.config.variants.find((variant) => String(variant.id) === String(this.config.selectedVariantId));
      return calculator.findVariant(this.config.variants, this.options.map((select) => select.value));
    }

    validateDimension(input, label) {
      const parsed = calculator.parseCentimetres(input.value);
      const message = parsed.ok ? '' : input.value.trim() === ''
        ? this.config.text[label === 'Length' ? 'enterLengthCm' : 'enterDepthCm']
        : this.config.text.invalidCm;
      input.setCustomValidity(message);
      const show = Boolean(message && input.dataset.touched);
      input.setAttribute('aria-invalid', String(show));
      const error = this.querySelector('#' + input.getAttribute('data-error-id'));
      if (error) { error.textContent = show ? message : ''; error.hidden = !show; }
      return parsed;
    }

    update(variantChanged = false) {
      const variant = this.selectedVariant();
      const length = this.validateDimension(this.lengthInput, 'Length');
      const depth = this.validateDimension(this.depthInput, 'Depth');
      const result = calculator.calculate(length.ok ? length.metres : '', depth.ok ? depth.metres : '');
      const sale = calculator.saleState(variant, this.config.pricePending, this.config.recipePending);
      this.currentResult = result;
      this.currentVariant = variant;
      this.session.setMeasurement(length.ok ? length.metres : '', depth.ok ? depth.metres : '', variant);
      if (this.session.current && this.config.productTitle) this.session.current.title = this.config.productTitle;
      const added = this.session.state === 'added';
      const uncertain = this.session.state === 'uncertain';
      const busy = this.client.busy;
      const locked = busy || added || uncertain;
      [this.lengthInput, this.depthInput, ...this.options].forEach((input) => { input.disabled = locked; });
      if (this.variantInput) { this.variantInput.value = variant ? String(variant.id) : ''; this.variantInput.disabled = !sale.allowed; }
      if (this.quantityInput) { this.quantityInput.value = result.ok ? String(result.quantity) : ''; this.quantityInput.disabled = !result.ok || !sale.allowed; }
      if (this.allocationInput) this.allocationInput.value = this.session.current ? this.session.current.id : '';
      if (this.unitPrice) this.unitPrice.textContent = sale.hasPrice ? this.money.format(variant.price / 100) + ' / m²' : 'Price to be confirmed';
      const total = result.ok && sale.hasPrice ? calculator.totalMinorUnits(variant.price, result.quantity) : null;
      if (this.runTotal) this.runTotal.textContent = total === null ? '—' : this.money.format(total / 100);
      const hiddenValues = { length: length.ok ? length.metres : '', depth: depth.ok ? depth.metres : '', area: result.ok ? result.exactArea : '', quantity: result.ok ? String(result.quantity) : '' };
      for (const [key, value] of Object.entries(hiddenValues)) {
        const input = this.querySelector('[data-border-property-' + key + ']');
        if (input) input.value = value;
      }
      this.button.hidden = false;
      this.button.disabled = busy || uncertain || (!added && (!sale.allowed || !result.ok || total === null));
      this.button.textContent = this.session.pending ? 'Adding run…' : added ? 'Add another run' : uncertain ? 'Check your basket' : result.ok ? 'Add ' + result.quantity.toLocaleString() + ' m² to basket' : 'Add this run to basket';
      this.button.title = !sale.allowed ? sale.message : '';
      if (this.feedback) this.feedback.textContent = this._message || '';
      this.form.setAttribute('aria-busy', String(this.session.pending));
      const reviewValues = {
        measurements: result.ok ? length.centimetres + ' cm × ' + depth.centimetres + ' cm' : '—',
        area: result.ok ? result.quantity.toLocaleString() + ' m²' : '—',
        plants: result.ok ? (BigInt(result.quantity) * BigInt(this.querySelectorAll('[data-border-plant-quantity]').length)).toLocaleString() : '—'
      };
      Object.entries(reviewValues).forEach(([key, value]) => { const node = this.querySelector('[data-border-review-' + key + ']'); if (node) node.textContent = value; });
      this.updatePlantCounts(result);
      this.updateTreeRecommendation();
      if (variantChanged && variant) {
        const link = Array.from(this.querySelectorAll('[data-border-thumbnail]')).find((item) => item.dataset.imageId === String(variant.imageId));
        if (link) this.selectImage(link);
      }
    }

    updatePlantCounts(result) {
      const quantities = this.querySelectorAll('[data-border-plant-quantity]');
      const summary = this.querySelector('[data-border-plant-summary]');
      const text = this.config.text;
      const quantity = result.ok ? result.quantity : null;
      quantities.forEach((node) => {
        node.textContent = quantity === null ? text.plantQuantityDefault : quantity === 1 ? text.plantQuantityOne : text.plantQuantity.replace('__COUNT__', quantity.toLocaleString());
      });
      if (summary) summary.textContent = quantity === null ? text.plantSummaryDefault : text.plantSummary
        .replace('__AREA__', quantity.toLocaleString())
        .replace('__PLANTS__', (BigInt(quantity) * BigInt(quantities.length)).toLocaleString());
    }

    selectImage(link) {
      const image = this.querySelector('[data-border-main-image]');
      const mainLink = this.querySelector('[data-border-main-link]');
      if (!image || !mainLink) return;
      image.src = link.dataset.imageSrc;
      image.srcset = link.dataset.imageSrcset;
      image.alt = link.dataset.imageAlt;
      image.width = Number(link.dataset.imageWidth);
      image.height = Number(link.dataset.imageHeight);
      mainLink.href = link.href;
      this.querySelectorAll('[data-border-thumbnail]').forEach((item) => {
        if (item === link) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      this.scheduleGalleryImage();
      if (this.galleryDialog && this.galleryDialog.open) this.openGalleryImage();
    }

    initialiseGallery(listener) {
      const link = this.querySelector('[data-border-main-link]');
      this.galleryDialog = this.querySelector('[data-border-gallery-dialog]');
      if (!link || !this.galleryDialog || typeof this.galleryDialog.showModal !== 'function') return;
      link.removeAttribute('target'); link.removeAttribute('rel');
      link.setAttribute('aria-haspopup', 'dialog');
      link.setAttribute('aria-label', 'Expand collection image');
      link.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        event.preventDefault(); this.openGalleryImage();
      }, listener);
      for (const type of ['pointerover', 'focus', 'pointerdown']) link.addEventListener(type, () => this.prepareGalleryImage(), listener);
      this.querySelector('[data-border-gallery-close]').addEventListener('click', () => this.galleryDialog.close(), listener);
      this.galleryDialog.addEventListener('click', event => { if (event.target === this.galleryDialog) this.galleryDialog.close(); }, listener);
      this.galleryDialog.addEventListener('close', () => { this._galleryRequest = (this._galleryRequest || 0) + 1; link.focus({preventScroll: true}); }, listener);
      this.scheduleGalleryImage();
    }

    scheduleGalleryImage() {
      clearTimeout(this._galleryTimer);
      if (!this.galleryDialog || root.navigator?.connection?.saveData) return;
      this._galleryTimer = setTimeout(() => { if (this.isConnected) this.prepareGalleryImage(); }, 650);
    }

    prepareGalleryImage(priority = 'low') {
      const source = this.querySelector('[data-border-main-link]').href;
      if (this._galleryPrepared && this._galleryPrepared.source === source) {
        this._galleryPrepared.image.fetchPriority = priority;
        return this._galleryPrepared;
      }
      if (this._galleryPrepared) this._galleryPrepared.cancel();
      const image = new root.Image();
      image.decoding = 'async'; image.fetchPriority = priority;
      const entry = {source, image, ready: false};
      entry.promise = new Promise(resolve => {
        let cancelled = false;
        entry.cancel = () => { cancelled = true; image.onload = image.onerror = null; image.removeAttribute('src'); resolve(null); };
        image.onload = () => {
          const decoded = image.decode ? image.decode() : Promise.resolve();
          decoded.catch(() => {}).then(() => { if (!cancelled) { entry.ready = true; resolve(entry); } });
        };
        image.onerror = () => { if (this._galleryPrepared === entry) this._galleryPrepared = null; resolve(null); };
      });
      this._galleryPrepared = entry;
      image.src = source;
      return entry;
    }

    openGalleryImage() {
      const image = this.querySelector('[data-border-main-image]');
      const expanded = this.querySelector('[data-border-gallery-image]');
      const entry = this.prepareGalleryImage('high');
      const request = this._galleryRequest = (this._galleryRequest || 0) + 1;
      const preview = image.currentSrc && image.currentSrc.split('?')[0] === image.src.split('?')[0] ? image.currentSrc : image.src;
      expanded.src = entry.ready ? entry.source : preview;
      expanded.alt = image.alt; expanded.width = image.width; expanded.height = image.height;
      if (!this.galleryDialog.open) this.galleryDialog.showModal();
      if (!entry.ready) entry.promise.then(loaded => {
        if (loaded && this.isConnected && this.galleryDialog.open && this._galleryRequest === request && this._galleryPrepared === entry) expanded.src = entry.source;
      });
    }

    async submit(event) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.client.busy) return;
      if (this.session.state === 'added') {
        if (!this.session.nextRun()) return;
        [this.lengthInput, this.depthInput].forEach((input) => { input.value = ''; delete input.dataset.touched; });
        this._message = '';
        this.update();
        this.lengthInput.focus({ preventScroll: true });
        return;
      }
      this.lengthInput.dataset.touched = 'true';
      this.depthInput.dataset.touched = 'true';
      this.update();
      if (!this.form.reportValidity() || this.button.disabled) return;
      const item = this.session.beginBorder();
      if (!item) return;
      this._message = '';
      const request = this.client.add(item);
      this.update();
      try {
        const addedItem = await request;
        const record = this.session.confirmBorder(addedItem);
        this._message = 'Run ' + record.number + ' added to your basket.';
        this.renderRuns();
        this.dispatchEvent(new CustomEvent('ho:border-added', { bubbles: true, detail: { item: addedItem } }));
        this.scheduleTreeScroll();
      } catch (error) {
        this.session.failBorder({ uncertain: error.uncertain !== false });
        this._message = this.cartErrorMessage(error);
      } finally { this.update(); }
    }

    scheduleTreeScroll() {
      if (!this.treeRecommendation) return;
      if (this._scrollController) this._scrollController.abort();
      const controller = this._scrollController = new AbortController();
      const timer = setTimeout(() => {
        controller.abort();
        if (this.isConnected) this.scrollTo(this.treeRecommendation);
      }, 2000);
      controller.signal.addEventListener('abort', () => clearTimeout(timer), { once: true });
      ['wheel', 'touchstart', 'pointerdown', 'keydown'].forEach((type) => root.addEventListener(type, () => controller.abort(), { once: true, passive: true, signal: controller.signal }));
    }

    scrollTo(element) {
      if (element) element.scrollIntoView({ block: 'start', behavior: root.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    cartErrorMessage(error) {
      const description = error && error.message ? error.message : 'The basket could not be updated.';
      return error && error.uncertain === false ? description : description + ' Check your basket before trying again.';
    }

    renderRuns() {
      if (!this.runList || !this.runs) return;
      this.runList.replaceChildren();
      const groups = new Map();
      this.session.records.forEach((run) => groups.set(run.id, { run, border: true, trees: [] }));
      this.session.treeRecords.forEach((tree) => {
        if (!groups.has(tree.run.id)) groups.set(tree.run.id, { run: tree.run, border: false, trees: [] });
        groups.get(tree.run.id).trees.push(tree);
      });
      for (const { run, border, trees } of groups.values()) {
        const entry = document.createElement('li'); entry.className = 'ho-border__run-entry';
        const title = document.createElement('strong'); title.className = 'ho-border__run-heading';
        title.textContent = border ? 'Run ' + run.number + ' added' : 'Trees for run ' + run.number + ' added';
        const detail = document.createElement('span'); detail.className = 'ho-border__run-detail';
        detail.textContent = run.result.length + ' m × ' + run.result.depth + ' m' + (border ? ' · ' + run.result.quantity.toLocaleString() + ' m² · ' + this.money.format(run.total / 100) : ' · border not yet added');
        entry.append(title, detail);
        for (const tree of trees) {
          const treeLine = document.createElement('span'); treeLine.className = 'ho-border__run-trees';
          treeLine.textContent = tree.quantity.toLocaleString() + ' × ' + tree.title + (tree.variantTitle ? ' — ' + tree.variantTitle : '') + ' · ' + this.money.format(tree.total / 100);
          entry.append(treeLine);
        }
        this.runList.append(entry);
      }
      this.runs.hidden = !groups.size;
    }

    initialiseTrees(listener) {
      const query = (selector) => this.querySelector(selector);
      this.treeRecommendation = query('[data-border-tree-recommendation]');
      this.treeChoices = Array.from(this.querySelectorAll('[data-border-tree-choice]'));
      this.treeOpen = this.treeChoices[0];
      this.treeDialog = query('[data-border-tree-dialog]');
      this.treeForm = query('[data-border-tree-form]');
      this.treeQuantity = query('[data-border-tree-quantity]');
      this.treeAdd = query('[data-border-tree-add]');
      this.treeFeedback = query('[data-border-tree-feedback]');
      this.treeLoading = query('[data-border-tree-loading]');
      this.treeCache = new Map();
      this.treeOptions = [];
      this._treeMessage = '';
      this._treeUncertain = this._treeUncertain || false;
      this._treeAddedFor = this._treeAddedFor || new Set();
      if (!this.treeChoices.length || !this.treeDialog || !this.treeForm) return;
      const jump = query('[data-border-tree-jump]');
      if (jump) jump.addEventListener('click', (event) => { event.preventDefault(); this.scrollTo(this.treeRecommendation); }, listener);
      if (this.config.tree && this.config.tree.handle) this.treeCache.set(this.config.tree.handle, this.config.tree);
      this.treeChoices.forEach((button) => button.addEventListener('click', () => this.chooseTree(button, listener), listener));
      const showAll = query('[data-border-tree-show-all]');
      if (showAll) showAll.addEventListener('click', () => {
        const expanded = showAll.getAttribute('aria-expanded') !== 'true';
        showAll.setAttribute('aria-expanded', String(expanded));
        showAll.textContent = expanded ? this.config.text.showFewerTrees : this.config.text.showAllTrees;
        this.querySelectorAll('[data-border-tree-more]').forEach((card) => { card.hidden = !expanded; });
      }, listener);
      const close = query('[data-border-tree-close]');
      if (close) close.addEventListener('click', () => this.treeDialog.close(), listener);
      this.treeDialog.addEventListener('close', () => {
        this._treeRequest = (this._treeRequest || 0) + 1;
        const target = this.treeOpen && this.treeOpen.disabled ? this.basketLink : this.treeOpen;
        if (target && target.isConnected) target.focus();
      }, listener);
      if (this.treeFeedback) {
        if (!this.treeFeedback.id) this.treeFeedback.id = this.id + '-tree-quantity-feedback';
        this.treeQuantity.setAttribute('aria-describedby', this.treeFeedback.id);
      }
      this.treeQuantity.addEventListener('input', () => { this.treeQuantity.dataset.touched = 'true'; this._treeMessage = ''; this.updateTree(); }, listener);
      this.treeQuantity.addEventListener('blur', () => { this.treeQuantity.dataset.touched = 'true'; this.updateTree(); }, listener);
      this.treeForm.addEventListener('submit', (event) => this.submitTrees(event), { ...listener, capture: true });
    }

    async chooseTree(button, listener) {
      const metadata = this.config.trees.find((tree) => tree.handle === button.dataset.borderTreeChoice);
      if (!metadata || button.disabled) return;
      if (typeof this.treeDialog.showModal !== 'function') { root.location.assign(metadata.url); return; }
      this.treeOpen = button;
      const request = this._treeRequest = (this._treeRequest || 0) + 1;
      this.treeForm.hidden = true;
      this.treeLoading.hidden = false;
      this.treeLoading.textContent = this.config.text.treesLoading;
      this.querySelector('[data-border-tree-title]').textContent = metadata.title;
      this.querySelector('[data-border-tree-context]').textContent = '';
      const image = this.querySelector('[data-border-tree-image]');
      image.hidden = !metadata.image;
      if (metadata.image) { image.src = metadata.image; image.alt = metadata.title; }
      if (!this.treeDialog.open) this.treeDialog.showModal();
      try {
        let tree = this.treeCache.get(metadata.handle);
        if (!tree) {
          const url = new URL(metadata.url, root.location.href);
          if (url.origin !== root.location.origin) throw new Error('Invalid product URL.');
          const response = await root.fetch(url.pathname.replace(/\/$/, '') + '.js', { credentials: 'same-origin', signal: this._controller.signal });
          if (!response.ok) throw new Error('Could not load this product.');
          tree = calculator.normaliseTree(await response.json(), metadata);
          this.treeCache.set(metadata.handle, tree);
        }
        if (metadata.variantCount > tree.variants.length) throw new Error('Full variety options are unavailable.');
        if (request !== this._treeRequest || !this.treeDialog.open) return;
        if (this._selectedTreeHandle !== tree.handle) this.configureTree(tree, listener);
        this.treeChoices.forEach((choice) => choice.setAttribute('aria-pressed', String(choice === button)));
        this.treeLoading.hidden = true;
        this.treeForm.hidden = false;
        this.openTrees();
      } catch (error) {
        if (request === this._treeRequest && this.isConnected) this.treeLoading.textContent = this.config.text.treesError;
      }
    }

    configureTree(tree, listener) {
      const query = (selector) => this.querySelector(selector);
      this.config.tree = tree;
      this._selectedTreeHandle = tree.handle;
      const selected = tree.variants.find((variant) => String(variant.id) === String(tree.selectedVariantId)) || tree.variants.find((variant) => calculator.saleState(variant, tree.pricePending, tree.recipePending).allowed) || tree.variants[0];
      const container = query('[data-border-tree-options]');
      container.replaceChildren();
      this.treeOptions = [];
      (tree.options || []).forEach((option, index) => {
        if (option.name === 'Title' && option.values.length === 1 && option.values[0] === 'Default Title') return;
        const label = document.createElement('label'); label.className = 'ho-border__tree-option';
        const caption = document.createElement('span'); caption.className = 'ho-border__tree-option-label'; caption.textContent = option.name;
        const select = document.createElement('select'); select.dataset.optionIndex = String(index);
        for (const value of option.values) { const item = document.createElement('option'); item.value = value; item.textContent = value; select.append(item); }
        if (selected && selected.options[index]) select.value = selected.options[index];
        select.addEventListener('change', () => { this._treeMessage = ''; this.updateTree(); }, listener);
        label.append(caption, select); container.append(label); this.treeOptions.push(select);
      });
      this.treeDefaultVariant = selected;
      const title = query('[data-border-tree-title]'); if (title) title.textContent = tree.title;
    }

    updateTreeRecommendation() {
      if (!this.treeRecommendation || !this.treeChoices) return;
      const measuredLength = calculator.parseCentimetres(this.lengthInput.value);
      const count = calculator.recommendTrees(measuredLength.ok ? measuredLength.metres : '');
      const alreadyAdded = this.session.current && this._treeAddedFor && this._treeAddedFor.has(this.session.current.id);
      this.treeChoices.forEach((button) => { button.disabled = this.client.busy || this.session.state === 'uncertain' || this._treeUncertain || alreadyAdded; });
      const note = this.querySelector('[data-border-tree-recommendation-note]');
      if (note) note.textContent = count > 0 ? this.config.text.treesRecommendation.replace('__COUNT__', count.toLocaleString()).replace('__LENGTH__', this.currentResult.length || (measuredLength.ok ? measuredLength.metres : '')) : this.config.text.treesMeasurementHint;
    }

    openTrees() {
      if (this.treeOpen.disabled) return;
      if (typeof this.treeDialog.showModal !== 'function') {
        this._message = 'Open the tree product page to choose your options.';
        this.update(); return;
      }
      this.treeRun = this.currentResult.ok && calculator.recommendTrees(this.currentResult.length) > 0 ? this.session.snapshot() : null;
      this._treeAdded = false;
      this._treeMessage = '';
      this.treeQuantity.value = this.treeRun ? String(calculator.recommendTrees(this.treeRun.result.length)) : '1';
      delete this.treeQuantity.dataset.touched;
      const context = this.querySelector('[data-border-tree-context]');
      if (context) context.textContent = this.treeRun ? 'For run ' + this.treeRun.number + ': ' + this.treeRun.result.length + ' m × ' + this.treeRun.result.depth + ' m. Trees are added separately.' : this.config.text.treesBeforeSizing;
      this.updateTree();
      if (!this.treeDialog.open) this.treeDialog.showModal();
      const focus = this.treeOptions[0] || this.treeQuantity;
      if (focus) focus.focus();
    }

    selectedTreeVariant() {
      if (!this.treeOptions.length) return this.treeDefaultVariant;
      const values = (this.treeDefaultVariant && this.treeDefaultVariant.options || []).slice();
      this.treeOptions.forEach((select) => { values[Number(select.dataset.optionIndex)] = select.value; });
      return calculator.findVariant(this.config.tree.variants, values);
    }

    updateTree() {
      if (!this.treeAdd) return;
      const variant = this.selectedTreeVariant();
      const quantity = calculator.parseQuantity(this.treeQuantity.value);
      const sale = calculator.saleState(variant, this.config.tree.pricePending, this.config.tree.recipePending);
      const total = sale.hasPrice && quantity !== null ? calculator.totalMinorUnits(variant.price, quantity) : null;
      const busy = this.client.busy;
      const locked = busy || this._treeAdded || this._treeUncertain;
      this.treeQuantity.disabled = locked;
      this.treeOptions.forEach((select) => { select.disabled = locked; });
      const quantityError = quantity === null ? 'Enter a whole number of trees, at least 1.' : '';
      const showQuantityError = Boolean(quantityError && this.treeQuantity.dataset.touched);
      this.treeQuantity.setCustomValidity(quantityError);
      this.treeQuantity.setAttribute('aria-invalid', String(showQuantityError));
      this.treeAdd.disabled = locked || !this.treeRun || !sale.allowed || total === null;
      this.treeAdd.textContent = this._treePending ? 'Adding trees…' : this._treeAdded ? 'Trees added to basket' : this._treeUncertain ? 'Check your basket' : this.config.text.treeAddToCart;
      const price = this.querySelector('[data-border-tree-price]');
      const totalNode = this.querySelector('[data-border-tree-total]');
      if (price) price.textContent = sale.hasPrice ? this.money.format(variant.price / 100) + ' per tree' : 'Price unavailable';
      if (totalNode) totalNode.textContent = total === null ? '—' : this.money.format(total / 100);
      if (this.treeFeedback) this.treeFeedback.textContent = this._treeMessage || (showQuantityError ? quantityError : !sale.allowed ? sale.message : !this.treeRun ? this.config.text.enterSizeFirst : '');
      const image = this.querySelector('[data-border-tree-image]');
      const source = variant && variant.image || this.config.tree.image;
      if (image) {
        const url = typeof source === 'string' ? source : source && (source.src || source.url);
        image.hidden = !url;
        if (url) { image.src = url; image.alt = source.alt || this.config.tree.title; }
      }
      this.treeForm.setAttribute('aria-busy', String(Boolean(this._treePending)));
    }

    async submitTrees(event) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.client.busy || this._treeAdded || this._treeUncertain) return;
      this.updateTree();
      if (!this.treeForm.reportValidity() || this.treeAdd.disabled || !this.treeRun) return;
      const variant = this.selectedTreeVariant();
      const quantity = calculator.parseQuantity(this.treeQuantity.value);
      const item = calculator.createTreeItem({ id: calculator.uniqueAllocation(root.crypto), variant, quantity, run: this.treeRun });
      this._treePending = true;
      this._treeMessage = '';
      const request = this.client.add(item);
      this.update(); this.updateTree();
      try {
        const addedItem = await request;
        this.session.confirmTrees({ run: this.treeRun, variant, quantity, title: this.config.tree.title, response: addedItem });
        this._treeAdded = true;
        this._treeAddedFor.add(this.treeRun.id);
        this._treeMessage = quantity.toLocaleString() + ' trees added to your basket.';
        this._message = this._treeMessage;
        this.renderRuns();
        this.dispatchEvent(new CustomEvent('ho:trees-added', { bubbles: true, detail: { item: addedItem } }));
      } catch (error) {
        this._treeUncertain = error.uncertain !== false;
        this._treeMessage = this.cartErrorMessage(error);
        this._message = this._treeMessage;
      } finally {
        this._treePending = false;
        this.update(); this.updateTree();
      }
    }

    disconnectedCallback() {
      if (this._scrollController) this._scrollController.abort();
      this._galleryRequest = (this._galleryRequest || 0) + 1;
      clearTimeout(this._galleryTimer);
      if (this._galleryPrepared) this._galleryPrepared.cancel();
      this._galleryPrepared = null;
      if (this._controller) this._controller.abort();
      this._connected = false;
      this._initialised = false;
    }
  }
  root.customElements.define('ho-ready-made-border', ReadyMadeBorder);
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';
  const MILLION = 1000000n;
  const MAX_SAFE = BigInt(Number.MAX_SAFE_INTEGER);
  function decimal(integer, places) {
    const value = integer.toString().padStart(places + 1, '0');
    const fraction = value.slice(-places).replace(/0+$/, '');
    return value.slice(0, -places) + (fraction ? '.' + fraction : '');
  }
  function parseMetres(value) {
    if (typeof value !== 'string') return { ok: false, reason: 'invalid' };
    const input = value.trim();
    if (input.length > 30 || !/^\d+(?:[.,]\d{1,3})?$/.test(input)) return { ok: false, reason: 'invalid' };
    const [whole, fraction = ''] = input.replace(',', '.').split('.');
    const millimetres = BigInt(whole) * 1000n + BigInt(fraction.padEnd(3, '0'));
    if (millimetres <= 0n) return { ok: false, reason: 'invalid' };
    return { ok: true, millimetres, metres: decimal(millimetres, 3) };
  }
  function parseCentimetres(value) {
    if (typeof value !== 'string') return { ok: false, reason: 'invalid' };
    const input = value.trim();
    if (input.length > 30 || !/^\d+(?:[.,]\d)?$/.test(input)) return { ok: false, reason: 'invalid' };
    const [whole, fraction = ''] = input.replace(',', '.').split('.');
    const millimetres = BigInt(whole) * 10n + BigInt(fraction || '0');
    if (millimetres <= 0n) return { ok: false, reason: 'invalid' };
    return { ok: true, millimetres, metres: decimal(millimetres, 3), centimetres: decimal(millimetres, 1) };
  }
  function calculate(lengthValue, depthValue) {
    const length = parseMetres(lengthValue), depth = parseMetres(depthValue);
    if (!length.ok || !depth.ok) return { ok: false, reason: 'invalid' };
    const area = length.millimetres * depth.millimetres;
    const rounded = (area + MILLION - 1n) / MILLION;
    if (rounded > MAX_SAFE) return { ok: false, reason: 'too-large' };
    return { ok: true, exactArea: decimal(area, 6), quantity: Number(rounded), length: length.metres, depth: depth.metres };
  }
  function totalMinorUnits(price, quantity) {
    if (!Number.isSafeInteger(price) || price <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0) return null;
    const total = BigInt(price) * BigInt(quantity);
    return total <= MAX_SAFE ? Number(total) : null;
  }
  function saleState(variant, pricePending, recipePending) {
    const hasPrice = Boolean(variant && Number.isSafeInteger(variant.price) && variant.price > 0 && !pricePending);
    if (recipePending) return { allowed: false, hasPrice, message: 'This collection is not available to order yet.' };
    if (pricePending || (variant && !hasPrice)) return { allowed: false, hasPrice: false, message: 'Price to be confirmed.' };
    if (!variant) return { allowed: false, hasPrice: false, message: 'This option is unavailable.' };
    if (!variant.available) return { allowed: false, hasPrice, message: 'This option is currently unavailable.' };
    return { allowed: true, hasPrice, message: '' };
  }
  function findVariant(variants, values) {
    return variants.find((variant) => variant.options.length === values.length && variant.options.every((value, index) => value === values[index]));
  }
  function normaliseTree(data, metadata) {
    if (!data || data.handle !== metadata.handle || !Array.isArray(data.options) || !Array.isArray(data.variants) || !data.variants.length || metadata.variantCount > data.variants.length) throw new Error('Full variety options are unavailable.');
    const variants = data.variants.map((variant) => ({
      id: String(variant.id), price: variant.price, available: variant.available === true,
      options: variant.options || [variant.option1, variant.option2, variant.option3].filter((value) => value !== null && value !== undefined),
      image: variant.featured_image && variant.featured_image.src || null
    }));
    if (variants.some((variant) => !/^\d+$/.test(variant.id) || !Number.isSafeInteger(variant.price) || !Array.isArray(variant.options) || variant.options.length !== data.options.length)) throw new Error('Invalid variety data.');
    const options = (data.options || []).map((option, index) => ({
      name: typeof option === 'string' ? option : option.name,
      values: [...new Set(variants.map((variant) => variant.options[index]))]
    }));
    const selected = variants.find((variant) => variant.available && variant.price > 0) || variants[0];
    return { ...metadata, variants, options, selectedVariantId: selected.id };
  }
  function recommendTrees(lengthValue) {
    const length = parseMetres(lengthValue);
    if (!length.ok) return null;
    const quantity = length.millimetres / 1200n;
    return quantity <= MAX_SAFE ? Number(quantity) : null;
  }
  function parseQuantity(value) {
    if (typeof value !== 'string' || !/^\d+$/.test(value.trim())) return null;
    const quantity = Number(value);
    return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : null;
  }
  function uniqueAllocation(crypto) {
    return crypto && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + '-' + Math.random().toString(36).slice(2);
  }
  function createRunItem({ id, variant, result, number }) {
    if (!id || !saleState(variant).allowed || !result || !result.ok || totalMinorUnits(variant.price, result.quantity) === null) throw new Error('Invalid border run.');
    return { id: String(variant.id), quantity: result.quantity, properties: {
      'Border run': String(number), 'Border length (m)': result.length, 'Border depth (m)': result.depth,
      'Measured area (m²)': result.exactArea, 'Ordered coverage (m²)': String(result.quantity), '_Border allocation': id
    } };
  }
  function createTreeItem({ id, variant, quantity, run }) {
    if (!id || !run || !run.id || !run.result || !saleState(variant).allowed || totalMinorUnits(variant.price, quantity) === null) throw new Error('Invalid tree selection.');
    return { id: String(variant.id), quantity, properties: {
      'For border run': String(run.number), 'Border length (m)': run.result.length, 'Border depth (m)': run.result.depth,
      ...(run.title ? { 'Border collection': run.title } : {}),
      '_Border allocation': run.id, '_Tree allocation': id
    } };
  }
  function cartAddEndpoint(configured, routeRoot) {
    const path = configured || (routeRoot || '/') + 'cart/add';
    if (!/^\/(?!\/)/.test(path)) throw new Error('Cart URL must be a relative shop route.');
    return path.endsWith('.js') ? path : path.replace(/\/$/, '') + '.js';
  }
  class CartAddError extends Error {
    constructor(message, uncertain = true) { super(message); this.name = 'CartAddError'; this.uncertain = uncertain; }
  }
  class CartAddClient {
    constructor({ fetch, endpoint, timeoutMs = 30000 }) { this.fetch = fetch; this.endpoint = endpoint; this.timeoutMs = timeoutMs; this.busy = false; }
    async add(item) {
      if (this.busy) throw new CartAddError('A basket update is already in progress.', false);
      this.busy = true;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), this.timeoutMs);
      try {
        let response, data;
        try {
          response = await this.fetch(this.endpoint, { method: 'POST', credentials: 'same-origin', signal: controller.signal, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }) });
          data = await response.json();
        } catch (error) { throw new CartAddError('The basket update could not be confirmed.'); }
        if (!response.ok) {
          const message = data && typeof data.description === 'string' ? data.description : data && typeof data.message === 'string' ? data.message : 'The item could not be added.';
          // A Shopify stock-limit 422 can add a partial quantity: it is not safe to retry.
          throw new CartAddError(message, ![400, 404].includes(response.status));
        }
        const items = data && Array.isArray(data.items) ? data.items : data && data.id ? [data] : [];
        const match = items.find((entry) => entry && String(entry.variant_id || entry.id) === String(item.id) && Number(entry.quantity) === item.quantity && entry.properties && entry.properties['_Border allocation'] === item.properties['_Border allocation'] && (!item.properties['_Tree allocation'] || entry.properties['_Tree allocation'] === item.properties['_Tree allocation']));
        if (!match) throw new CartAddError('The basket returned an unexpected result.');
        return match;
      } finally { clearTimeout(timeout); this.busy = false; }
    }
  }
  class RunSession {
    constructor({ makeId = () => uniqueAllocation() } = {}) {
      this.makeId = makeId; this.records = []; this.treeRecords = []; this.current = null; this.state = 'editing'; this.pending = false;
    }
    setMeasurement(length, depth, variant) {
      if (this.pending || this.state !== 'editing') return this.current;
      const result = calculate(length, depth);
      if (!result.ok || !variant) { this.current = null; return null; }
      const key = [String(variant.id), result.length, result.depth].join('|');
      if (!this.current || this.current.key !== key) {
        const usedNumbers = [0, ...this.records.map((run) => run.number), ...this.treeRecords.map((tree) => tree.run.number)];
        this.current = { id: this.makeId(), number: Math.max(...usedNumbers) + 1, key, result, variant: { ...variant } };
      } else this.current.variant = { ...variant };
      return this.current;
    }
    snapshot() { return this.current ? { ...this.current, result: { ...this.current.result }, variant: { ...this.current.variant } } : null; }
    beginBorder() {
      if (this.pending || this.state !== 'editing' || !this.current || !saleState(this.current.variant).allowed) return null;
      const item = createRunItem(this.current); this.pending = true; return item;
    }
    confirmBorder(response) {
      if (!this.pending || !this.current) throw new Error('No border add in progress.');
      const record = this.snapshot();
      const returnedTotal = response && response.final_line_price;
      record.total = Number.isSafeInteger(returnedTotal) && returnedTotal >= 0 ? returnedTotal : totalMinorUnits(record.variant.price, record.result.quantity);
      this.records.push(record); this.pending = false; this.state = 'added'; return record;
    }
    failBorder({ uncertain = false } = {}) { this.pending = false; this.state = uncertain ? 'uncertain' : 'editing'; }
    nextRun() {
      if (this.pending || this.state !== 'added') return false;
      this.current = null; this.state = 'editing'; return true;
    }
    confirmTrees({ run, variant, quantity, title, response }) {
      const returnedTotal = response && response.final_line_price;
      this.treeRecords.push({ run: { ...run, result: { ...run.result } }, quantity, title, variantTitle: variant.options.filter((value) => value !== 'Default Title').join(' / '), total: Number.isSafeInteger(returnedTotal) && returnedTotal >= 0 ? returnedTotal : totalMinorUnits(variant.price, quantity) });
    }
  }
  return { parseMetres, parseCentimetres, calculate, totalMinorUnits, saleState, findVariant, normaliseTree, recommendTrees, parseQuantity, uniqueAllocation, createRunItem, createTreeItem, cartAddEndpoint, CartAddClient, CartAddError, RunSession };
});




